/**
 * propertyGridForMarking — PURE resolver (AD-013, L1A staff-evaluation marking).
 *
 * THE LOCKED MODEL (AD-013 / grid-first sequencing):
 *   · the GRID is a PROPERTY-level, reusable, versioned asset — traced ONCE on the
 *     Property page (Concept ▸ Version ▸ Floor), stored as the version floor's
 *     `boundary` + `gridId` (+ a GridSnapshot under properties/{pid}/grids/).
 *   · ENTRANCE + AUO marks are ENGAGEMENT-SPECIFIC — they live in the project
 *     intake, NOT on the property; they gain a `gridId` FK to the property grid
 *     they were placed on.
 *
 * The project staff-evaluation panel (StaffTechnicalPanel, `hideGridTool`) must
 * therefore MARK on the property's already-traced grid — boundary LOCKED, marking
 * enabled. This resolver reads a linked property's ACTIVE grid (the exact same
 * concept ▸ version ▸ floor the owner traced on the Property page) and returns the
 * geometry the marking surface needs:
 *   - `boundary` (≥3 pts, IMAGE-PIXEL space of the active plan),
 *   - `northRotation` (the EXACT value the Property page fed to `computeGrid` —
 *      `floor.northRotation ?? property.northRotation ?? 0` — so the grid the staff
 *      mark on is byte-identical to the property grid: same boundary + same
 *      rotation ⇒ same padas/zones),
 *   - `floorPlan` (the active plan image — REQUIRED: the boundary coordinates are
 *      in THAT image's pixel space, so the tracer must render it for marks to
 *      align),
 *   - `gridId` (the FK stamped onto every saved mark).
 *
 * Returns `null` when the property has no traced grid yet (no active-version floor
 * carries a ≥3-pt boundary) → the panel shows a "trace this property's grid first
 * → Open grid on the Property page" pointer instead of a dead marking surface.
 *
 * PURE — no Firestore, no React. The caller passes an already-normalised
 * `LiveProperty` (every store read wraps `normalizeProperty`).
 */
import {
  activeConcept,
  activeVersion,
  versionFloors,
  type LiveProperty,
  type PropertyFloor,
  type Pt2D,
  type FloorId,
} from './propertyModel';
import type { FloorPlan } from './floorPlan';

export interface PropertyGridForMarking {
  /** the registry property (P#####) whose grid the marks are placed on. */
  propertyId: string;
  /** active concept the grid was traced under (provenance). */
  conceptId: string;
  /** active version the grid was traced under (provenance). */
  versionId: string;
  /** the gridable floor inside that version. */
  floorId: FloorId;
  /** human label (e.g. "Site / plot map", "Ground Floor"). */
  floorLabel: string;
  /** the traced boundary (≥3 pts) in the active plan's image-pixel space. */
  boundary: Pt2D[];
  /** the EXACT engine rotation the Property page used for this grid. */
  northRotation: number;
  /** FK → properties/{pid}/grids/{gridId}; null when the floor has a boundary but
   *  no gridId was stamped yet (legacy / pre-snapshot trace). */
  gridId: string | null;
  /** the active plan image — the substrate the boundary + marks are drawn on. */
  floorPlan: FloorPlan | null;
  /**
   * Gap #1 (2026-06-28) — TRUE for the PLAN-ONLY case: the active floor carries an
   * uploaded plan image but NO usable (≥3-pt) boundary yet. `boundary` is empty and
   * `gridId` is null. The project surface SHOWS the map (with a "trace boundary →
   * Property page" link) instead of a dead pointer — VISIBILITY only. A plan-only
   * floor is NOT grid-ready and must NOT count as "evaluated".
   */
  needsBoundary: boolean;
}

/** PURE — the active plan image of a property floor, as a `FloorPlan`, or null. */
function activeFloorPlan(floor: PropertyFloor): FloorPlan | null {
  const v = (floor.plans ?? []).find((p) => p.isActive);
  if (!v) return null;
  return {
    storagePath: v.storagePath,
    downloadURL: v.imageUrl,
    contentType: v.contentType,
    width: v.width,
    height: v.height,
  };
}

/** PURE — does this floor carry a usable, grid-able boundary (≥3 pts)? */
function isGridable(floor: PropertyFloor): boolean {
  return Array.isArray(floor.boundary) && floor.boundary.length >= 3;
}

/**
 * PURE — resolve a property's ACTIVE grid for engagement-side marking, or null
 * when nothing (no boundary AND no plan) exists yet. Picks the active concept →
 * active version → the FIRST gridable floor, preferring a floor that already has a
 * `gridId` (a confirmed snapshot) over a bare boundary.
 *
 * Gap #1 — when NO floor is gridable yet but the active version has a floor with an
 * uploaded PLAN, returns a PLAN-ONLY shape (`needsBoundary: true`, empty boundary)
 * so the project surface can SHOW the map (+ a "trace boundary" link) instead of a
 * dead pointer. Plan-only is visibility-only — not grid-ready, not "evaluated".
 */
export function resolvePropertyGridForMarking(
  prop: LiveProperty | null | undefined,
): PropertyGridForMarking | null {
  if (!prop) return null;
  const concept = activeConcept(prop);
  if (!concept) return null;
  const version = activeVersion(concept);
  if (!version) return null;
  const floors = versionFloors(version);
  const gridable = floors.filter(isGridable);
  if (gridable.length > 0) {
    // prefer a floor with a stamped gridId (a confirmed snapshot) over a bare trace.
    const floor = gridable.find((f) => !!f.gridId) ?? gridable[0];
    const northRotation = floor.northRotation ?? prop.northRotation ?? 0;
    return {
      propertyId: prop.propertyId,
      conceptId: concept.conceptId,
      versionId: version.versionId,
      floorId: floor.floorId,
      floorLabel: floor.label || floor.floorId,
      boundary: floor.boundary as Pt2D[],
      northRotation,
      gridId: floor.gridId ?? null,
      floorPlan: activeFloorPlan(floor),
      needsBoundary: false,
    };
  }

  // TOP-LEVEL FALLBACK (recover pre-existing traces): some grids are written ONLY
  // to the top-level `floors[]` mirror — the candidate→registry push
  // (`savePropertyFloorBoundary`, candidateRegistryPush) and the legacy
  // PropertyFloorCard path both do so. On a property that was ALREADY versioned
  // (concepts[].versions present), `normalizeProperty` is a no-op, so such a
  // boundary never reaches the active-version floor the resolver reads above and
  // the trace stops resolving ("boundary not traced yet") even though it exists.
  // Before degrading to plan-only, honour a gridable top-level floor so those
  // pre-existing traced grids keep resolving. The floorId is shared across the
  // mirror, so the active plan image still comes from the version floor.
  const topFloors = (prop.floors ?? []) as PropertyFloor[];
  const topGridable = topFloors.find(isGridable);
  if (topGridable) {
    const versionMatch = floors.find((f) => f.floorId === topGridable.floorId);
    const planSource = versionMatch ?? topGridable;
    return {
      propertyId: prop.propertyId,
      conceptId: concept.conceptId,
      versionId: version.versionId,
      floorId: topGridable.floorId,
      floorLabel: topGridable.label || versionMatch?.label || topGridable.floorId,
      boundary: topGridable.boundary as Pt2D[],
      northRotation: topGridable.northRotation ?? prop.northRotation ?? 0,
      gridId: topGridable.gridId ?? null,
      floorPlan: activeFloorPlan(planSource),
      needsBoundary: false,
    };
  }

  // PLAN-ONLY (Gap #1) — no boundary yet, but the active version may carry a floor
  // with an uploaded plan. Surface the image so the project SHOWS the map.
  const planFloor = floors.find((f) => !!activeFloorPlan(f));
  if (!planFloor) return null;
  return {
    propertyId: prop.propertyId,
    conceptId: concept.conceptId,
    versionId: version.versionId,
    floorId: planFloor.floorId,
    floorLabel: planFloor.label || planFloor.floorId,
    boundary: [],
    northRotation: planFloor.northRotation ?? prop.northRotation ?? 0,
    gridId: null,
    floorPlan: activeFloorPlan(planFloor),
    needsBoundary: true,
  };
}
